import { useI18n } from '@/composables/useI18n'
// src/stores/theme.ts
// Real-time Dynamic CSS Variables Theme Store for iTorgo Design Suite
//
// The "Kyrgyz Gold" palette lives in src/styles/tailwind.css and is the single
// source of truth for the design language. This store may only LAYER admin
// customisations on top of it: any value that still matches the declared
// baseline is written as *nothing at all* (the inline override is removed), so
// the stylesheet keeps governing — including token forms this store cannot
// express, such as `--color-border: 229 226 218 / 0.9` with its alpha channel.

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient } from '@/services/api'

export interface ThemeSettings {
  // Category tile artwork (Admin > Design > Categories): iTorgo's own
  // vector art on a shyrdak tile, the same art on a coloured disc, or the
  // legacy Unsplash photos.
  categoryArtStyle?: 'shyrdak' | 'object' | 'photo'
  logoType: 'icon_text' | 'image' | 'text_only'
  logoUrl: string
  logoText: string
  logoTagline: string
  logoHeightPx: number
  logoBadgeShape: 'rounded' | 'square' | 'circle' | 'transparent'
  logoBadgeColor: string
  faviconUrl: string

  primaryColor: string
  primaryHoverColor: string
  secondaryColor: string
  secondaryHoverColor: string
  accentColor: string
  backgroundColor: string
  surfaceColor: string
  surfaceElevatedColor: string
  textPrimaryColor: string
  textSecondaryColor: string
  textMutedColor: string
  borderColor: string
  // Dark surfaces (hero board, footer, dark buttons): --color-ink.
  inkColor?: string
  // Secondary highlight ("sun"): deal chips, the home wash, the sun header.
  sunColor?: string
  // 'brand' = header band in the brand colour; 'light' = white header;
  // 'sun' = header band in the sun colour.
  headerStyle?: 'brand' | 'light' | 'sun'

  buttonRadius: '0px' | '6px' | '10px' | '16px' | '9999px'
  buttonShadow: 'none' | 'sm' | 'md' | 'lg' | 'glow'
  buttonHoverEffect: 'scale' | 'lift' | 'glow' | 'none'

  cardRadius: '8px' | '16px' | '24px'
  cardGlassBlur: 'none' | '10px' | '20px' | '40px'
  cardBorder: 'none' | 'subtle' | 'solid'
  cardShadow: 'none' | 'sm' | 'md' | 'lg'

  fontFamily: 'Poppins' | 'Inter' | 'Plus Jakarta Sans' | 'Montserrat' | 'Rubik'
  titleFontWeight: '600' | '700' | '800' | '900'

  activePreset: string
  updatedAt: string
}

export interface ThemePresetItem {
  id: string
  name: string
  description: string
  author: string
  version: string
  previewColors: string[]
  theme: Partial<ThemeSettings>
}

type ThemeColorKey =
  | 'primaryColor'
  | 'primaryHoverColor'
  | 'secondaryColor'
  | 'secondaryHoverColor'
  | 'accentColor'
  | 'backgroundColor'
  | 'surfaceColor'
  | 'surfaceElevatedColor'
  | 'textPrimaryColor'
  | 'textSecondaryColor'
  | 'textMutedColor'
  | 'borderColor'

/** The "Kyrgyz Gold" values declared on :root in src/styles/tailwind.css. */
const DESIGN_TOKENS: Record<ThemeColorKey, string> = {
  primaryColor: '#F4B410',        // --color-primary        244 180 16
  primaryHoverColor: '#E2A400',   // --color-primary-hover  226 164 0
  secondaryColor: '#14171F',      // --color-secondary      20 23 31 (Ink)
  secondaryHoverColor: '#262A36', // --color-secondary-hover 38 42 54
  accentColor: '#F0EEE8',         // --color-accent         240 238 232 warm neutral fill
  backgroundColor: '#F7F6F2',     // --color-background     247 246 242 warm paper ground
  surfaceColor: '#FFFFFF',        // --color-surface        255 255 255
  surfaceElevatedColor: '#FFFFFF',// --color-surface-elevated
  textPrimaryColor: '#14171F',    // --color-text-primary   20 23 31
  textSecondaryColor: '#52545C',  // --color-text-secondary 82 84 92
  textMutedColor: '#787A82',      // --color-text-muted     120 122 130
  borderColor: '#E5E2DA'          // --color-border         229 226 218 / 0.9 warm outline
}

/**
 * Values persisted by the pre-"Kyrgyz Gold" design language (a cold zinc ramp on
 * pure white plus a cornflower-blue secondary). A stored theme that still carries
 * these exact values was never customised by an admin — it is the old default —
 * so it is migrated to the current design tokens instead of being painted over
 * the palette. Anything an admin actually picked passes through untouched.
 */
const LEGACY_PALETTE: Partial<Record<ThemeColorKey, string>> = {
  primaryColor: '#F2B138',
  primaryHoverColor: '#E09E22',
  secondaryColor: '#5B9BD5',
  secondaryHoverColor: '#4787C4',
  accentColor: '#F4F4F5',
  backgroundColor: '#FFFFFF',
  textPrimaryColor: '#18181B',
  textSecondaryColor: '#52525B',
  textMutedColor: '#71717A',
  borderColor: '#E4E4E7'
}

/** '#abc' | 'abcdef' -> '#AABBCC'. Returns '' when the input is not a hex colour. */
function normalizeHex(hex: string): string {
  if (!hex) return ''
  let cleanHex = String(hex).trim().replace('#', '')
  if (cleanHex.length === 3) {
    cleanHex = cleanHex.split('').map(c => c + c).join('')
  }
  if (!/^[0-9a-fA-F]{6}$/.test(cleanHex)) return ''
  return `#${cleanHex.toUpperCase()}`
}

function sameHex(a: string, b: string): boolean {
  const left = normalizeHex(a)
  return left !== '' && left === normalizeHex(b)
}

function hexToRgbValues(hex: string): string {
  const cleanHex = normalizeHex(hex).replace('#', '')
  if (!cleanHex) return '244 180 16'
  const r = parseInt(cleanHex.substring(0, 2), 16)
  const g = parseInt(cleanHex.substring(2, 4), 16)
  const b = parseInt(cleanHex.substring(4, 6), 16)
  return `${r} ${g} ${b}`
}

/** Blend `hex` toward `target` by `amount` (0..1) — used for derived tints/shades. */
function mixHex(hex: string, target: string, amount: number): string {
  const from = normalizeHex(hex).replace('#', '')
  const to = normalizeHex(target).replace('#', '')
  if (!from || !to) return normalizeHex(hex)
  const channel = (offset: number) => {
    const a = parseInt(from.substring(offset, offset + 2), 16)
    const b = parseInt(to.substring(offset, offset + 2), 16)
    return Math.round(a + (b - a) * amount).toString(16).padStart(2, '0')
  }
  return `#${channel(0)}${channel(2)}${channel(4)}`.toUpperCase()
}

/** WCAG relative luminance of a #RRGGBB colour. */
function luminance(hex: string): number {
  const h = normalizeHex(hex).replace('#', '')
  if (!h) return 0
  const ch = (i: number) => {
    const c = parseInt(h.substring(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * ch(0) + 0.7152 * ch(2) + 0.0722 * ch(4)
}

function contrastRatio(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((m, n) => n - m)
  return (x + 0.05) / (y + 0.05)
}

/** Dark ink or white — whichever reads better on the given fill. */
function onColorFor(fill: string, ink = '#14171F'): string {
  return contrastRatio(fill, ink) >= contrastRatio(fill, '#FFFFFF') ? ink : '#FFFFFF'
}

/**
 * Write a colour token only when it actually differs from the design-language
 * baseline; otherwise clear the inline override so tailwind.css wins.
 */
function writeToken(root: HTMLElement, cssVar: string, hex: string, baseline: string) {
  const value = normalizeHex(hex)
  if (!value || sameHex(value, baseline)) {
    root.style.removeProperty(cssVar)
    return
  }
  root.style.setProperty(cssVar, hexToRgbValues(value))
}

export const useThemeStore = defineStore('theme', () => {
  const defaultTheme: ThemeSettings = {
    logoType: 'icon_text',
    logoUrl: '',
    logoText: 'iTorgo',
    logoTagline: 'Real-Time Platform',
    logoHeightPx: 40,
    logoBadgeShape: 'rounded',
    logoBadgeColor: DESIGN_TOKENS.primaryColor,
    faviconUrl: '/favicon.ico',

    primaryColor: DESIGN_TOKENS.primaryColor,
    primaryHoverColor: DESIGN_TOKENS.primaryHoverColor,
    secondaryColor: DESIGN_TOKENS.secondaryColor,
    secondaryHoverColor: DESIGN_TOKENS.secondaryHoverColor,
    accentColor: DESIGN_TOKENS.accentColor,
    backgroundColor: DESIGN_TOKENS.backgroundColor,
    surfaceColor: DESIGN_TOKENS.surfaceColor,
    surfaceElevatedColor: DESIGN_TOKENS.surfaceElevatedColor,
    textPrimaryColor: DESIGN_TOKENS.textPrimaryColor,
    textSecondaryColor: DESIGN_TOKENS.textSecondaryColor,
    textMutedColor: DESIGN_TOKENS.textMutedColor,
    borderColor: DESIGN_TOKENS.borderColor,

    buttonRadius: '10px',
    buttonShadow: 'md',
    buttonHoverEffect: 'lift',

    cardRadius: '16px',
    cardGlassBlur: '20px',
    cardBorder: 'subtle',
    cardShadow: 'md',

    fontFamily: 'Poppins',
    titleFontWeight: '800',

    activePreset: 'sunlit_gold',
    updatedAt: new Date().toISOString()
  }

  const theme = ref<ThemeSettings>({ ...defaultTheme })
  const publishedTheme = ref<ThemeSettings>({ ...defaultTheme })
  const presets = ref<Record<string, ThemePresetItem>>({})
  const isLoading = ref(false)
  const isSaving = ref(false)
  const isInitialized = ref(false)

  const isDirty = computed(() => {
    return JSON.stringify(theme.value) !== JSON.stringify(publishedTheme.value)
  })

  /** Upgrade a stored theme that still carries the old design language. */
  function migrateStoredTheme(t: ThemeSettings): ThemeSettings {
    const next: ThemeSettings = { ...t }
    ;(Object.keys(DESIGN_TOKENS) as ThemeColorKey[]).forEach(key => {
      const legacy = LEGACY_PALETTE[key]
      if (!normalizeHex(next[key]) || (legacy && sameHex(next[key], legacy))) {
        next[key] = DESIGN_TOKENS[key]
      }
    })
    // The paper ground and the card surface may never collapse into one colour —
    // "white cards lift without heavy shadows" depends on that separation.
    if (sameHex(next.backgroundColor, next.surfaceColor)) {
      next.backgroundColor = DESIGN_TOKENS.backgroundColor
    }
    return next
  }

  function applyThemeToDOM(t: ThemeSettings) {
    if (typeof document === 'undefined') return
    const root = document.documentElement

    const primary = normalizeHex(t.primaryColor) || DESIGN_TOKENS.primaryColor
    const secondary = normalizeHex(t.secondaryColor) || DESIGN_TOKENS.secondaryColor
    const accent = normalizeHex(t.accentColor) || DESIGN_TOKENS.accentColor

    // Primary Colors
    writeToken(root, '--color-primary', primary, DESIGN_TOKENS.primaryColor)
    writeToken(root, '--color-primary-hover', t.primaryHoverColor || primary, DESIGN_TOKENS.primaryHoverColor)

    // The light-gold chip is a pale TINT of the brand colour, never the
    // full-strength gold — bg-primaryContainer is paired with the dark
    // text-onPrimaryContainer, which is calibrated for a tint.
    if (sameHex(primary, DESIGN_TOKENS.primaryColor)) {
      root.style.removeProperty('--color-primary-container')
      root.style.removeProperty('--color-on-primary-container')
      root.style.removeProperty('--color-primary-muted')
    } else {
      root.style.setProperty('--color-primary-container', hexToRgbValues(mixHex(primary, '#FFFFFF', 0.8)))
      root.style.setProperty('--color-on-primary-container', hexToRgbValues(mixHex(primary, '#000000', 0.5)))
      root.style.setProperty('--color-primary-muted', `${hexToRgbValues(primary)} / 0.14`)
    }

    // Text on brand fills follows the brand colour's contrast, so any palette
    // (light gold, sky blue, a dark custom colour) keeps readable buttons.
    const onPrimary = onColorFor(primary)
    if (sameHex(onPrimary, '#14171F')) root.style.removeProperty('--color-on-primary')
    else root.style.setProperty('--color-on-primary', hexToRgbValues(onPrimary))

    // Dark surfaces (hero board, footer, dark buttons) follow the palette's ink.
    const ink = normalizeHex(t.inkColor || '') || '#14171F'
    // Brand-coloured text on those dark surfaces: lighten the brand colour
    // until it reaches 4.5:1 against the ink (a deep blue would disappear).
    let onDark = primary
    for (let i = 0; i < 12 && contrastRatio(onDark, ink) < 5; i++) onDark = mixHex(onDark, '#FFFFFF', 0.15)
    // 5:1 (not 4.5) leaves headroom for translucent chips laid over the ink.
    root.style.setProperty('--color-primary-on-dark', hexToRgbValues(onDark))
    // Phone browser bar follows the palette's ink (was a fixed gold).
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', ink)
    if (sameHex(ink, '#14171F')) {
      root.style.removeProperty('--color-ink')
      root.style.removeProperty('--color-ink-soft')
    } else {
      root.style.setProperty('--color-ink', hexToRgbValues(ink))
      root.style.setProperty('--color-ink-soft', hexToRgbValues(mixHex(ink, '#FFFFFF', 0.1)))
    }

    // Sun highlight: text on it is whichever of ink/white reads better
    // (a yellow always takes the ink).
    const sun = normalizeHex(t.sunColor || '') || '#FFD43B'
    root.style.setProperty('--color-sun', hexToRgbValues(sun))
    root.style.setProperty('--color-on-sun', hexToRgbValues(onColorFor(sun, ink)))

    // Secondary Colors
    writeToken(root, '--color-secondary', secondary, DESIGN_TOKENS.secondaryColor)
    writeToken(root, '--color-secondary-hover', t.secondaryHoverColor || secondary, DESIGN_TOKENS.secondaryHoverColor)
    if (sameHex(secondary, DESIGN_TOKENS.secondaryColor)) {
      root.style.removeProperty('--color-secondary-muted')
    } else {
      root.style.setProperty('--color-secondary-muted', `${hexToRgbValues(secondary)} / 0.08`)
    }

    // Neutral fill (chips, inputs) — kept in the same family as the ground so
    // warm accents can never end up sitting inside cold borders.
    writeToken(root, '--color-accent', accent, DESIGN_TOKENS.accentColor)
    if (sameHex(accent, DESIGN_TOKENS.accentColor)) {
      root.style.removeProperty('--color-accent-hover')
    } else {
      root.style.setProperty('--color-accent-hover', hexToRgbValues(mixHex(accent, '#000000', 0.035)))
    }

    // Background & Surface
    writeToken(root, '--color-background', t.backgroundColor, DESIGN_TOKENS.backgroundColor)
    writeToken(root, '--color-surface', t.surfaceColor, DESIGN_TOKENS.surfaceColor)
    writeToken(
      root,
      '--color-surface-elevated',
      t.surfaceElevatedColor || t.surfaceColor,
      DESIGN_TOKENS.surfaceElevatedColor
    )

    // Text & Border
    writeToken(root, '--color-text-primary', t.textPrimaryColor, DESIGN_TOKENS.textPrimaryColor)
    writeToken(root, '--color-text-secondary', t.textSecondaryColor, DESIGN_TOKENS.textSecondaryColor)
    writeToken(root, '--color-text-muted', t.textMutedColor, DESIGN_TOKENS.textMutedColor)
    writeToken(root, '--color-border', t.borderColor, DESIGN_TOKENS.borderColor)

    // Button & Card Design Tokens
    root.style.setProperty('--btn-radius', t.buttonRadius)
    root.style.setProperty('--card-radius', t.cardRadius)

    // NOTE: the theme's `fontFamily` is intentionally NOT injected into the DOM.
    // The app root carries the static Tailwind `font-sans` utility, which resolves
    // to 'Golos Text' and beats anything inherited from <body>, so the dynamic
    // Google Fonts <link> only downloaded a whole family (18 Poppins faces) that
    // rendered nowhere. Re-enabling it requires tailwind.config.js to point
    // fontFamily.sans at var(--font-sans) first.

    // Favicon update if provided
    if (t.faviconUrl) {
      const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement | null
      if (link) link.href = t.faviconUrl
    }
  }

  async function initTheme() {
    if (isInitialized.value) return
    isLoading.value = true
    try {
      const { data: res } = await apiClient.get<any>('/api/admin/theme')
      if (res && res.success && res.data) {
        theme.value = migrateStoredTheme({ ...defaultTheme, ...res.data })
        publishedTheme.value = { ...theme.value }
        applyThemeToDOM(theme.value)
      }
      const { data: presetsRes } = await apiClient.get<any>('/api/admin/theme/presets')
      if (presetsRes && presetsRes.success && presetsRes.data) {
        presets.value = presetsRes.data
      }
      isInitialized.value = true
    } catch (err) {
      console.warn('[ThemeStore] Failed to fetch theme from server, using default:', err)
      applyThemeToDOM(theme.value)
    } finally {
      isLoading.value = false
    }
  }

  function setLivePreview(partial: Partial<ThemeSettings>) {
    theme.value = { ...theme.value, ...partial }
    applyThemeToDOM(theme.value)
  }

  function revertPreview() {
    theme.value = { ...publishedTheme.value }
    applyThemeToDOM(theme.value)
  }

  function applyPreset(presetKey: string) {
    const preset = presets.value[presetKey]
    if (!preset) return
    theme.value = {
      ...theme.value,
      ...preset.theme,
      activePreset: presetKey
    }
    applyThemeToDOM(theme.value)
  }

  async function publishTheme(): Promise<{ success: boolean; message?: string }> {
    isSaving.value = true
    try {
      const { data: res } = await apiClient.put<any>('/api/admin/theme', theme.value)
      if (res && res.success) {
        publishedTheme.value = { ...res.data }
        theme.value = { ...res.data }
        applyThemeToDOM(theme.value)
        return { success: true, message: res.message || useI18n().t('toasts.success') }
      }
      throw new Error('Theme update failed')
    } catch (err: any) {
      console.error('[ThemeStore] Error publishing theme:', err)
      throw err
    } finally {
      isSaving.value = false
    }
  }

  function resetToDefault() {
    theme.value = { ...defaultTheme }
    applyThemeToDOM(theme.value)
  }

  return {
    theme,
    publishedTheme,
    presets,
    isLoading,
    isSaving,
    isDirty,
    isInitialized,
    initTheme,
    setLivePreview,
    revertPreview,
    applyPreset,
    publishTheme,
    resetToDefault
  }
})
